import { DotMatrix } from '@clickguard/design-system'
import { forwardRef, useImperativeHandle } from 'react'
import {
	Elements,
	PaymentElement,
	useStripe,
	useElements
} from '@stripe/react-stripe-js'
import { useBillingStore } from '../../../../../../store/billing-store/billing-store'
import { PaymentFormProps, PaymentFormRef } from './types'
import { useThemeColors } from 'hooks'

const themeColorDefinitions = {
	background: { variable: '--background', fallback: '#ffffff' },
	text: { variable: '--stripe-text', fallback: '#30313d' },
	placeholder: { variable: '--stripe-placeholder', fallback: '#77787D' },
	textSecondary: { variable: '--stripe-text-secondary', fallback: '#6D6E78' },
	inputBorder: { variable: '--t-stripe-input', fallback: '#e6e6e6' },
	terms: { variable: '--t-desc-gray', fallback: '#535255' }
} as const

interface StripePaymentElementProps {
	onPaymentComplete?: (isComplete: boolean) => void
	onPaymentError?: (error?: string) => void
	onSelectedPmTypeChange?: (type: string) => void
}

const StripePaymentElement = forwardRef<
	PaymentFormRef,
	StripePaymentElementProps
>(({ onPaymentComplete, onPaymentError, onSelectedPmTypeChange }, ref) => {
	const stripe = useStripe()
	const elements = useElements()

	useImperativeHandle(ref, () => ({
		confirmPayment: async () => {
			if (!stripe || !elements) return { kind: 'aborted' as const }

			onPaymentError?.(undefined)

			// Deferred-intent / manual PM creation flow: <Elements> is mounted
			// without a clientSecret, so we just validate locally with
			// elements.submit() and then create an unattached PaymentMethod.
			// The BE handles attach + verification (delayed-attach pattern), so
			// failures don't leave orphan PMs on the customer.
			const { error: submitError } = await elements.submit()
			if (submitError) {
				const msg = submitError.message || 'Please check your card details'
				onPaymentError?.(msg)
				return { kind: 'error' as const, error: msg }
			}

			const { error, paymentMethod } = await stripe.createPaymentMethod({
				elements
			})
			if (error) {
				const msg = error.message || 'Unexpected error, please try again'
				onPaymentError?.(msg)
				return { kind: 'error' as const, error: msg }
			}
			if (!paymentMethod) {
				return { kind: 'error' as const, error: 'Card verification failed' }
			}

			return { kind: 'ok' as const, pmToken: paymentMethod.id }
		},
		handleNextAction: async (clientSecret: string) => {
			if (!stripe) return { ok: false, error: 'Stripe not ready' }
			const result = await stripe.handleNextAction({ clientSecret })
			if (result.error) {
				return {
					ok: false,
					error: result.error.message || 'Card authentication failed'
				}
			}
			return { ok: true }
		}
	}))

	return (
		<PaymentElement
			options={{
				layout: 'tabs',
				fields: {
					billingDetails: {
						// @ts-expect-error Stripe supports 'always'; stripe-js 7.x types omit it.
						name: 'always'
					}
				}
			}}
			onChange={(event) => {
				onPaymentComplete?.(event.complete)
				// The parent uses the selected method for payment-specific copy.
				if (event.value?.type) onSelectedPmTypeChange?.(event.value.type)
			}}
			onFocus={() => onPaymentError?.(undefined)}
		/>
	)
})

StripePaymentElement.displayName = 'StripePaymentElement'

export const PaymentForm = forwardRef<PaymentFormRef, PaymentFormProps>(
	({ onPaymentComplete, onPaymentError, onSelectedPmTypeChange }, ref) => {
		// Manual PaymentMethod creation mode — no SI binding, no clientSecret.
		// PaymentElement stays mounted across submits; createPaymentMethod
		// produces a fresh unattached PM each time. The BE handles attach +
		// pre-auth + mandate setup so failures never leave orphan PMs on the
		// customer.
		const { stripe } = useBillingStore()
		const themeColors = useThemeColors(themeColorDefinitions)

		return (
			<div className="bg-white">
				{/* Stripe PaymentElement */}
				<div className="flex flex-col gap-8">
					{stripe ? (
						<Elements
							stripe={stripe}
							options={{
								mode: 'setup',
								currency: 'usd',
								paymentMethodCreation: 'manual',
								// Hide manual entry / microdeposit verification under the
								// us_bank_account tab — the trial-start round trip can't
								// host microdeposits, so manual entry would just fail at
								// confirmMandateSetupIntent. Only Financial Connections
								// (instant verification) is supported end-to-end.
								paymentMethodOptions: {
									us_bank_account: {
										verification_method: 'instant'
									}
								},
								appearance: {
									theme: 'stripe',
									variables: {
										colorPrimary: '#554BFD',
										colorBackground: themeColors.background,
										colorText: themeColors.text,
										colorTextPlaceholder: themeColors.placeholder,
										colorTextSecondary: themeColors.textSecondary,
										colorDanger: '#df1b41',
										fontFamily: '"Host Grotesk", sans-serif',
										borderRadius: '5px',
										fontSizeBase: '16px',
										fontSizeSm: '14.88px',
										spacingGridRow: '12px',
										spacingGridColumn: '12px',
										spacingTab: '8px'
									},
									rules: {
										'.Tab': {
											border: `1px solid ${themeColors.inputBorder}`,
											boxShadow:
												'0px 3px 6px rgba(0,0,0,0.02), 0px 1px 1px rgba(0,0,0,0.03)',
											borderRadius: '5px',
											padding: '12px',
											fontSize: '14px',
											fontWeight: '600',
											letterSpacing: '0.56px'
										},
										'.Tab--selected': {
											backgroundColor: '#554BFD',
											borderColor: '#554BFD',
											color: '#ffffff',
											boxShadow: 'none'
										},
										'.Tab--selected:hover': {
											backgroundColor: '#554BFD',
											borderColor: '#554BFD',
											color: '#FFFFFF'
										},
										'.Tab:hover': {
											borderColor: '#554BFD'
										},
										'.TabIcon--selected': {
											fill: '#ffffff'
										},
										'.Input': {
											border: `1px solid ${themeColors.inputBorder}`,
											boxShadow:
												'0px 3px 6px rgba(0,0,0,0.02), 0px 1px 1px rgba(0,0,0,0.03)',
											padding: '12px',
											fontSize: '16px',
											lineHeight: '1.45'
										},
										'.Input:focus': {
											borderColor: '#554BFD',
											boxShadow: '0 0 0 1px #554BFD'
										},
										'.Label': {
											fontSize: '14.88px',
											fontWeight: '400',
											lineHeight: '1.45',
											color: themeColors.text
										},
										'.TermsText': {
											color: themeColors.terms,
											fontFamily: '"Host Grotesk", sans-serif',
											fontSize: '12px',
											fontStyle: 'normal',
											fontWeight: '400',
											lineHeight: '18px'
										}
									}
								}
							}}
						>
							<StripePaymentElement
								ref={ref}
								onPaymentComplete={onPaymentComplete}
								onPaymentError={onPaymentError}
								onSelectedPmTypeChange={onSelectedPmTypeChange}
							/>
						</Elements>
					) : (
						<div className="min-h-[200px] flex items-center justify-center">
							<DotMatrix size="sm" />
						</div>
					)}
				</div>
			</div>
		)
	}
)

PaymentForm.displayName = 'PaymentForm'
